import React from 'react';
import { DashboardCard } from '../components/DashboardCard';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
  Box,
} from '@mui/material';
import { FafsaDataProvider, useFafsaData } from '../contexts';
import { FafsaFilterControls } from './FafsaFilterControls';

const FafsaTableContent = () => {
  const { data, loading, error, filter, totalsByYear, grandTotal } = useFafsaData();

  if (loading) {
    return <Skeleton variant="rounded" height={300} />;
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary">
        No FAFSA data available for selected years
      </Typography>
    );
  }

  //Get unique years from data
  const years = [...new Set(data.map(d => d.year))].sort();

  const renderTableContent = () => {
    if (!filter.groupBySchool) {
      // Simple view - one row for all schools combined
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>School Year</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Submitted
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Total Enrollment
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Submission Rate
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {years.map(year => {
                const yearData = data.find(d => d.year === year);
                if (!yearData) return null;
                
                return (
                  <TableRow key={year}>
                    <TableCell>{`${year}-${year + 1}`}</TableCell>
                    <TableCell align="right">{yearData.submittedCount}</TableCell>
                    <TableCell align="right">{yearData.totalEnrollment}</TableCell>
                    <TableCell align="right">
                      <strong>{yearData.percentageSubmitted.toFixed(1)}%</strong>
                    </TableCell>
                  </TableRow>
                );
              })}
              {years.length > 1 && grandTotal && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {grandTotal.totalSubmitted}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {grandTotal.totalEnrollment}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {grandTotal.totalPercentage}%
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      //Grouped by school view
      const schools = [...new Set(data.map(d => d.schoolName))].sort();
      
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>School</TableCell>
                {years.map(year => (
                  <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                    {`${year}-${year + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {schools.map(school => (
                <TableRow key={school}>
                  <TableCell>{school}</TableCell>
                  {years.map(year => {
                    const cellData = data.find(
                      d => d.year === year && d.schoolName === school
                    );
                    
                    if (!cellData) {
                      return <TableCell key={year} align="center">-</TableCell>;
                    }
                    
                    return (
                      <TableCell key={year} align="center">
                        {`${cellData.submittedCount} (${cellData.percentageSubmitted.toFixed(1)}%)`}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                {years.map(year => {
                    const yearTotal = totalsByYear.find(t => t.year === year);
                    if (!yearTotal) {
                        return <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>-</TableCell>;
                    }
                  
                  return (
                    <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                        {`${yearTotal.totalSubmitted} (${yearTotal.totalPercentage}%)`}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  return (
    <Box sx={{ height: '100%' }}>
      <FafsaFilterControls />
      {renderTableContent()}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
         Note: Counts and submission rates are calculated using 12th grade students only
      </Typography>
    </Box>
  );
};

export const FafsaTableCard = () => (
  <FafsaDataProvider>
    <DashboardCard title="FAFSA Submission">
      <FafsaTableContent />
    </DashboardCard>
  </FafsaDataProvider>
);